import { useState, useEffect } from 'react';

import { Button } from '@/components/common/Button';
import { usePropertyStore } from '@/stores/property-store';
import { SHOPPING_CATEGORIES } from '@/types/shopping-list';
import type { ShoppingListItem, CreateShoppingListItemInput, UpdateShoppingListItemInput } from '@/types/shopping-list';

interface ShoppingItemFormProps {
  /** Existing item when editing; undefined when adding */
  item?: ShoppingListItem;
  /** Pre-fill taskId (when opened from task detail) */
  defaultTaskId?: string;
  /** Pre-fill propertyId */
  defaultPropertyId?: string;
  onSubmit: (data: CreateShoppingListItemInput | UpdateShoppingListItemInput) => Promise<void>;
  onClose: () => void;
}

export function ShoppingItemForm({
  item,
  defaultTaskId,
  defaultPropertyId,
  onSubmit,
  onClose,
}: ShoppingItemFormProps) {
  const { properties, loadProperties } = usePropertyStore();

  const [name, setName] = useState(item?.name ?? '');
  const [category, setCategory] = useState(item?.category ?? 'other');
  const [quantity, setQuantity] = useState(item?.quantity != null ? String(item.quantity) : '');
  const [unit, setUnit] = useState(item?.unit ?? '');
  const [propertyId, setPropertyId] = useState(item?.propertyId ?? defaultPropertyId ?? '');
  const [notes, setNotes] = useState(item?.notes ?? '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nameError, setNameError] = useState('');

  useEffect(() => {
    if (properties.length === 0) void loadProperties();
  }, [properties.length, loadProperties]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!name.trim()) {
      setNameError('Item name is required');
      return;
    }
    setNameError('');
    setIsSubmitting(true);

    try {
      const quantityNum = quantity.trim() ? parseFloat(quantity) : undefined;
      const data: CreateShoppingListItemInput = {
        name: name.trim(),
        category: category || 'other',
        quantity: quantityNum !== undefined && !isNaN(quantityNum) ? quantityNum : undefined,
        unit: unit.trim() || undefined,
        propertyId: propertyId || undefined,
        taskId: item?.taskId ?? defaultTaskId ?? undefined,
        notes: notes.trim() || undefined,
        purchased: item?.purchased ?? false,
        purchasedAt: item?.purchasedAt,
      };
      await onSubmit(data);
      onClose();
    } catch (err) {
      console.error('ShoppingItemForm.handleSubmit:', err);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/60" data-testid="shopping-item-form-overlay">
      <div className="bg-[var(--color-elevated)] border border-[var(--color-border)] rounded-xl shadow-card-lg w-full max-w-sm flex flex-col" data-testid="shopping-item-form">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-[var(--color-border)]">
          <h2 className="font-display text-display-sm text-[var(--color-text-display)]">
            {item ? 'Edit Item' : 'Add Item'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
            aria-label="Close form"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 px-5 py-4 overflow-y-auto max-h-[70vh]">
          {/* Item name */}
          <div className="flex flex-col gap-1">
            <label className="field-label" htmlFor="item-name">
              Item *
            </label>
            <input
              id="item-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Brassica seed mix"
              className="w-full px-3 py-2 rounded-md bg-[var(--color-surface)] border border-[var(--color-border)] text-body-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
              data-testid="item-name-input"
              autoFocus
            />
            {nameError && (
              <p className="text-body-xs text-red-400" data-testid="name-error">{nameError}</p>
            )}
          </div>

          {/* Category */}
          <div className="flex flex-col gap-1">
            <label className="field-label" htmlFor="item-category">
              Category
            </label>
            <select
              id="item-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2 rounded-md bg-[var(--color-surface)] border border-[var(--color-border)] text-body-sm text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
              data-testid="item-category-select"
            >
              {SHOPPING_CATEGORIES.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          {/* Quantity + Unit */}
          <div className="flex gap-3">
            <div className="flex flex-col gap-1 flex-1">
              <label className="field-label" htmlFor="item-quantity">
                Qty
              </label>
              <input
                id="item-quantity"
                type="number"
                min="0"
                step="any"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="0"
                className="w-full px-3 py-2 rounded-md bg-[var(--color-surface)] border border-[var(--color-border)] text-body-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
                data-testid="item-quantity-input"
              />
            </div>
            <div className="flex flex-col gap-1 flex-1">
              <label className="field-label" htmlFor="item-unit">
                Unit
              </label>
              <input
                id="item-unit"
                type="text"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                placeholder="lbs, bags, boxes…"
                className="w-full px-3 py-2 rounded-md bg-[var(--color-surface)] border border-[var(--color-border)] text-body-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
                data-testid="item-unit-input"
              />
            </div>
          </div>

          {/* Property */}
          {properties.length > 0 && (
            <div className="flex flex-col gap-1">
              <label className="field-label" htmlFor="item-property">
                Property
              </label>
              <select
                id="item-property"
                value={propertyId}
                onChange={(e) => setPropertyId(e.target.value)}
                className="w-full px-3 py-2 rounded-md bg-[var(--color-surface)] border border-[var(--color-border)] text-body-sm text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
                data-testid="item-property-select"
              >
                <option value="">— Any property —</option>
                {properties.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Notes */}
          <div className="flex flex-col gap-1">
            <label className="field-label" htmlFor="item-notes">
              Notes
            </label>
            <textarea
              id="item-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes…"
              rows={2}
              className="w-full px-3 py-2 rounded-md bg-[var(--color-surface)] border border-[var(--color-border)] text-body-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] resize-none"
              data-testid="item-notes-input"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
              className="flex-1"
              data-testid="submit-button"
            >
              {isSubmitting ? 'Saving…' : item ? 'Save Changes' : 'Add to List'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={isSubmitting}
              data-testid="cancel-button"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
